import type {
  EmployeeGroupSchema,
  EmployeeGroupSchemaWithId,
} from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { EmployeeGroup } from "~/providers/employee-group-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useEmployeeGroupStore } from "~/stores/use-employee-group-store";

export const useEmployeeGroupManager = () => {
  const { t } = useTranslation("common");

  const { showDialog } = useAlertStore();

  // const { hasPermission } = usePermissionManager();

  const { setEmployeeGroupDialogOpen, setSelectedEmployeeGroup } =
    useEmployeeGroupStore();

  const { handleEmployeeGroupUpdate } = useEmployeeGroupUpdates();

  const { mutate: create } = api.employeeGroup.create.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(
          t("create_item_success", { item: t("EmployeeGroup.employee_group") }),
        ),
      );
      await handleEmployeeGroupUpdate(result);
    },
    onError() {
      toast.error(
        capitalize(
          t("create_item_error", { item: t("EmployeeGroup.employee_group") }),
        ),
      );
    },
  });

  const { mutate: update } = api.employeeGroup.update.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(
          t("update_item_success", { item: t("EmployeeGroup.employee_group") }),
        ),
      );
      await handleEmployeeGroupUpdate(variables.id);
    },
    onError() {
      toast.error(
        capitalize(
          t("update_item_error", { item: t("EmployeeGroup.employee_group") }),
        ),
      );
    },
  });

  const { mutate: deleteEmployeeGroup } =
    api.employeeGroup.deleteEmployeeGroup.useMutation({
      onSuccess() {
        toast.success(
          t("delete_item_success", { item: t("EmployeeGroup.employee_group") }),
        );
      },
    });

  const createEmployeeGroup = async (
    data: z.infer<ReturnType<typeof EmployeeGroupSchema>>,
  ) => {
    // const { permission } = await hasPermission({
    //   requiredPermission: Permissions.create_employee,
    //   label: t("Permissions.create_employee_group_label"),
    // });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof EmployeeGroupSchema>>>
    >((resolve) => {
      // if (!permission)
      //   return resolve({
      //     error: { path: "root", message: t("Permissions.no_permission") },
      //   });

      create(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("EmployeeGroup.employee_group"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updateEmployeeGroup = async (
    data: z.infer<ReturnType<typeof EmployeeGroupSchemaWithId>>,
  ) => {
    // const { permission } = await hasPermission({
    //   requiredPermission: Permissions.create_section,
    //   label: t("Permissions.update_employee_group_label"),
    // });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof EmployeeGroupSchema>>>
    >((resolve) => {
      // if (!permission)
      //   return resolve({
      //     error: { path: "root", message: t("Permissions.no_permission") },
      //   });

      update(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", {
                  item: t("EmployeeGroup.employee_group"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const deleteEmployeeGroupHandler = async (employeeGroup: EmployeeGroup) => {
    // const { permission } = await hasPermission({
    //   requiredPermission: Permissions.delete_employee_group,
    // });

    const result = await showDialog({
      title: t("EmployeeGroup.before_delete_title", {
        name: employeeGroup.name,
      }),
      message: t("EmployeeGroup.before_delete_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    return new Promise<boolean>((resolve) => {
      // if (!permission) {
      //   resolve(false);
      // }

      if (result === "cancel") {
        resolve(false);
      }

      deleteEmployeeGroup(employeeGroup.id, {
        onSuccess: () => {
          resolve(true);
        },
      });
    });
  };

  const openEmployeeGroupDialog = (employeeGroup?: EmployeeGroup) => {
    setSelectedEmployeeGroup(employeeGroup);
    setEmployeeGroupDialogOpen(true);
  };

  return {
    openEmployeeGroupDialog,
    updateEmployeeGroup,
    createEmployeeGroup,
    deleteEmployeeGroup: deleteEmployeeGroupHandler,
  };
};

export const useEmployeeGroupUpdates = () => {
  const utils = api.useUtils();

  const handleEmployeeGroupUpdate = useCallback(
    async (employeeGroup: string) => {
      const result = await utils.employeeGroup.getById.fetch(employeeGroup);

      void utils.employeeGroup.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.employeeGroup.getAll, utils.employeeGroup.getById],
  );

  const handleEmployeeGroupDelete = useCallback(
    (employeeGroup: string) => {
      void utils.employeeGroup.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== employeeGroup);
      });
    },
    [utils.employeeGroup.getAll],
  );

  return {
    handleEmployeeGroupUpdate,
    handleEmployeeGroupDelete,
  };
};
